<template>
  <main>
    <div data-scroll-el="pdpContainer" />
    <div :class="{ 'fit-finder-container': $feature.showFitFinderOnPDP }">
      <slot name="notifications" />
      <transition name="fade">
        <div v-if="productPending && !product">
          <pdp-skeleton />
        </div>
        <div v-else-if="product" class="pb-6 space-y-6 lg:pb-8 lg:space-y-8">
          <div class="lg:mb-10 lg:flex lg:items-start">
            <section class="relative lg:grow" data-scroll-el="gallery">
              <pdp-section-breadcrumbs
                class="absolute left-0 top-0 z-1 m-2 rounded-xs bg-grey-80 bg-op-80 px-2 py-1 lg:!hidden"
              />
              <template v-if="galleryPending">
                <div class="lg:grid lg:cols-2 lg:grow lg:gap-2 lg:pl-6 <lg:hidden">
                  <div
                    v-for="i in 6"
                    :key="i"
                    class="skeleton"
                    :class="{ 'col-span-2': [3, 4].includes(i) }"
                    :style="{ 'aspect-ratio': presets.src.width / presets.src.height }"
                  />
                </div>
                <div
                  class="skeleton lg:hidden"
                  :style="{ 'aspect-ratio': presets.src.width / presets.src.height }"
                />
              </template>
              <slot v-else name="gallery" />
            </section>
            <section
              v-style="{ w: { lg: '30rem' }, top: { lg: '6rem' } }"
              class="px-4 lg:sticky lg:shrink-0 lg:px-6 lg:pt-12 space-y-2"
            >
              <div class="f-col gap-4 <lg:hidden">
                <div>
                  <slot name="breadcrumbs" />
                </div>
                <div class="space-y-4">
                  <pdp-section-heading-title :is-page-title="false" />
                  <pdp-section-heading-details />
                  <slot name="espot-below-pricing" />
                </div>
              </div>
              <pdp-section-gift-card v-if="isDigitalProduct">
                <div class="mt-4 b-t b-grey-70 pt-4 lg:hidden space-y-4">
                  <pdp-section-heading-title />
                  <pdp-section-heading-details />
                  <slot name="espot-below-pricing" />
                </div>
              </pdp-section-gift-card>
              <pdp-section-variant-selection v-else>
                <div class="mt-4 b-t b-grey-70 pt-4 lg:hidden space-y-4">
                  <pdp-section-heading-title />
                  <pdp-section-heading-details />
                  <slot name="espot-below-pricing" />
                </div>
              </pdp-section-variant-selection>

              <div v-if="showSizeChart" class="mt-2 body-4 underlined">
                <slot name="size-chart" />
              </div>
              <div v-if="$feature.showSizeAdvice" class="mt-4 body-4">
                <slot name="size-advice" />
              </div>

              <div v-if="!$viewport.lg" class="mt-4">
                <slot name="buy-with-prime" />
              </div>
              <slot name="espot-below-sizes" />
              <div v-if="$feature.bundleUpsellsPlacementOnPdp" class="!mt-4">
                <slot name="bundle-upsells" />
              </div>
              <template v-if="!isDigitalProduct">
                <slot name="cta-add-to-cart" />
                <slot name="cta-apple-pay" />
              </template>
              <p v-if="showLegalDisclaimer && allowApplePayOrderOnPDP" class="mt-2 text-xs">
                <form-legal-disclaimer
                  :content="$t.checkoutLegalText"
                  :data-test-ids="{
                    'sale-terms': 'pdp-terms-link',
                    'privacy': 'pdp-privacy-link',
                  }"
                />
              </p>
              <slot name="klarna-payments" />
              <div v-if="showFindInStore" class="body-4 underlined">
                <slot name="cta-find-in-store" />
              </div>
              <div v-if="!$feature.bundleUpsellsPlacementOnPdp" class="!mt-4">
                <slot name="bundle-upsells" />
              </div>
              <div class="w-full space-y-4">
                <div v-if="showFitFinder" class="body-4 underlined">
                  <slot name="fit-finder" />
                </div>
                <slot v-if="$viewport.lg" name="buy-with-prime" />
              </div>
              <slot name="paypal-payments" />
              <slot name="upsells" />
              <slot name="shipping-methods" />
            </section>
          </div>
          <div
            v-if="!isDigitalProduct"
            v-style:mx="{ lg: '4rem' }"
            class="px-4 pb-10 lg:px-6 lg:pb-16 <lg:pt-10"
          >
            <slot name="espot-top-of-secondary-section" />
            <slot name="accordions" />
            <slot name="espot-below-accordions" />
          </div>
          <slot name="shop-the-look" />
          <slot name="recommendations" />
          <slot name="espot-lazy-below-gallery" />
          <slot name="reviews" />
          <slot name="espot-lazy-above-footer" />
        </div>
      </transition>
    </div>
    <slot name="sticky-cta-bar" />
    <slot />
  </main>
</template>

<script lang="ts" setup>
import type { Slots } from '#commerce/types/pdp'

defineSlots<Slots>()

const {
  galleryPending,
  isDigitalProduct,
  product,
  productPending,
  showFindInStore,
  showFitFinder,
  showSizeChart
} = usePdp()
const { gallery: { presets }, showLegalDisclaimer } = useAppConfig().pages.pdp
const { allowApplePayOrderOnPDP } = useExpressPaymentFeatures()
</script>
